<script setup lang="ts">
    import { computed, onMounted, watch, ref, type Ref } from 'vue'
    import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
    import { EditorState, generateName } from '@common/Common'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import { guid } from '@common/Guid'
    import { CurrencyDenomination, MonthYear, MonthYearHelpers, ValidationError } from '@common/types'
    import { useCapexMetricConfigurationQueryV1 } from '@Quest/Metrics/queries-vue-composables'
    import { useCreateCapexMetricCommandV1, useUpdateCapexMetricCommandV1 } from '@Quest/Metrics/commands-vue-composables'
    import type { CapexMetricConfigurationReadModel } from '@Quest/Metrics/readmodels'
    import { ValueCommitmentType } from '@Quest/Metrics/types'
    import MetricTimeWindowSettings from '@Quest/Metrics/Components/MetricTimeWindowSettings.vue'
    import MetricScheduleDependentSettings from '@Quest/Metrics/Components/MetricScheduleDependentSettings.vue'

    const route = useRoute()
    const router = useRouter()

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params?.strategyId ?? ''))
    const pageMode = computed(() => route.meta.mode as EditorState)
    const isEditMode = computed(() => pageMode.value === EditorState.Editor)

    const {
        runCapexMetricConfigurationQueryV1,
        modelCapexMetricConfigurationQueryV1
    } = useCapexMetricConfigurationQueryV1(workspaceId, strategyId)

    const rm = modelCapexMetricConfigurationQueryV1 as Ref<CapexMetricConfigurationReadModel | null>


    const {
        invokeCreateCapexMetricCommandV1,
        validationErrorsCreateCapexMetricCommandV1,
        onSucceedCreateCapexMetricCommandV1
    } = useCreateCapexMetricCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateCapexMetricCommandV1,
        validationErrorsUpdateCapexMetricCommandV1,
        onSucceedUpdateCapexMetricCommandV1
    } = useUpdateCapexMetricCommandV1(workspaceId, strategyId)

    // ======================
    // State
    // ======================

    const initialized = ref(false)
    const errors = ref<ValidationError[]>([])
    const isSaving = ref(false)
    const allowRouteLeave = ref(false)
    const isScheduleDependent = ref(false)

    const name = ref<string | null>(null)
    const description = ref<string | null>(null)
    const accountId = ref<string | null>(null)
    const isDepreciation = ref(false)
    const denomination = ref<CurrencyDenomination>(CurrencyDenomination.Unit)

    const windowStartOffsetInMonths = ref<number | null>(null)
    const windowLengthInMonths = ref<number | null>(null)

    const startDateBoundary = ref<MonthYear | null>(null)
    const endDateBoundary = ref<MonthYear | null>(null)
    const defaultProjectStartDate = ref<MonthYear | null>(null)

    const valueCommitmentType = ref<ValueCommitmentType>(ValueCommitmentType.Total)

    const pageHeading = computed(() => isEditMode.value ? "Edit the metric" : "New capex metric")
    const saveButtonText = computed(() => isSaving.value ? "Saving..." : "Save changes")
    const nameError = computed(() => errors.value.find(e => e.ErrorCode.startsWith("Name."))?.Error ?? null)

    const hasChanges = computed(() => {
        if (!isEditMode.value) return true

        const model = rm.value
        if (!model) return false

        const scheduleEnabled = isScheduleDependent.value
        const scheduleExists = model.ScheduleDependentSettings != null

        if (scheduleEnabled !== scheduleExists)
            return true

        // base fields (common to both modes)
        if (
            name.value !== model.Name ||
            description.value !== model.Description ||
            accountId.value !== model.AccountId ||
            denomination.value !== model.Denomination ||
            windowStartOffsetInMonths.value !== model.WindowStartOffsetInMonths ||
            windowLengthInMonths.value !== model.WindowLengthInMonths
        ) {
            return true
        }

        if (scheduleEnabled) {
            const s = model.ScheduleDependentSettings

            return (
                valueCommitmentType.value !== s?.ValueCommitmentType ||
                !MonthYearHelpers.areEqual(defaultProjectStartDate.value, s?.DefaultProjectStartDate) ||
                !MonthYearHelpers.areEqual(startDateBoundary.value, s?.StartDateBoundary) ||
                !MonthYearHelpers.areEqual(endDateBoundary.value, s?.EndDateBoundary)
            )
        }

        // non-schedule specific field
        return isDepreciation.value !== model.IsDepreciation
    })


    function handleSave()
    {
        isSaving.value = true
        errors.value = []

        const isUpdate = isEditMode.value && rm.value

        const payload = {
            id: isUpdate ? rm.value!.Id : guid(),
            name: name.value,
            description: description.value,
            denomination: denomination.value,
            windowStartOffsetInMonths: windowStartOffsetInMonths.value,
            windowLengthInMonths: windowLengthInMonths.value,
            accountId: accountId.value,
            isDepreciation: isDepreciation.value,
            isScheduleDependent: isScheduleDependent.value,
            defaultProjectStartDate: defaultProjectStartDate.value,
            startDateBoundary: startDateBoundary.value,
            endDateBoundary: endDateBoundary.value,
            valueCommitmentType: valueCommitmentType.value
        }

        if (isUpdate) {
            invokeUpdateCapexMetricCommandV1(payload)
        } else {
            invokeCreateCapexMetricCommandV1(payload)
        }
    }

    function handleCancel()
    {
        if (isEditMode.value)
        {
            router.go(0)
        } else
        {
            router.go(-1)
        }
    }

    function handleBack()
    {
        router.go(-1)
    }


    onMounted(() =>
    {
        document.title = "Capex Metric Configuration"
        runCapexMetricConfigurationQueryV1({ MetricId: route.params.metricId ?? null })
    })

    watch(rm, (model) =>
    {
        if (!model || initialized.value) return

        name.value = isEditMode.value ? model.Name : generateName(model.Name)
        description.value = model.Description
        accountId.value = model.AccountId ?? null
        isDepreciation.value = model.IsDepreciation
        denomination.value = model.Denomination
        windowStartOffsetInMonths.value = model.WindowStartOffsetInMonths
        windowLengthInMonths.value = model.WindowLengthInMonths

        isScheduleDependent.value = model.ScheduleDependentSettings != null
        valueCommitmentType.value = model.ScheduleDependentSettings?.ValueCommitmentType ?? ValueCommitmentType.Total

        defaultProjectStartDate.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.DefaultProjectStartDate)
        startDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.StartDateBoundary)
        endDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.EndDateBoundary)

        initialized.value = true
    })


    onSucceedCreateCapexMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(-1)
    })

    onSucceedUpdateCapexMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(0)
    })

    watch(validationErrorsCreateCapexMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    watch(validationErrorsUpdateCapexMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    onBeforeRouteLeave((_to, _from, next) =>
    {
        if (allowRouteLeave.value)
        {
            next()
            return
        }

        if (hasChanges.value && !isSaving.value)
        {
            if (confirm('You have unsaved changes. Do you want to save them before leaving?'))
            {
                handleSave()
            }
            else
            {
                next()
            }
        } else
        {
            next()
        }
    })
</script>

<template>
    <div>
        <CorporateNavigation ActivePage="metrics" />

        <div v-if="initialized" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6>
                        <a class="text-dark" @click="handleBack" data-testid="capex-metric-config-back">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back
                        </a>
                    </h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">{{pageHeading}}</h1>
                </div>

                <div class="col-lg-6 text-right align-self-end">
                    <button type="submit" form="resource-metric-configuration-form" class="mr-1" :disabled="isSaving"
                            data-testid="capex-metric-config-save"
                            :class="hasChanges ? 'btn btn-warning' : 'btn btn-outline-secondary'" style="padding: 0.7em 2em;">
                        {{ saveButtonText }}
                    </button>
                    <button v-if="hasChanges" type="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="handleCancel" data-testid="capex-metric-config-cancel">
                        Cancel
                    </button>
                </div>
            </div>

            <hr class="mb-5" />

            <form id="resource-metric-configuration-form" @submit.prevent="handleSave" class="mb-4" data-testid="capex-metric-config-form">
                <div class="form-group mb-4">
                    <label>Metric Name:</label>
                    <input v-model.trim="name" type="text" class="form-control" required maxlength="100" minlength="2" data-testid="capex-metric-config-name" />
                    <div class="error">{{nameError}}</div>
                </div>

                <div class="form-group mb-4">
                    <label>Metric Description:</label>
                    <textarea v-model.trim="description" class="form-control" data-testid="capex-metric-config-description"></textarea>
                </div>

                <div class="form-row">
                    <div class="form-group mb-4 col">
                        <label for="capex" class="form-label">Measured Capex:</label>
                        <select id="capex" v-model="accountId" class="form-control" data-testid="capex-metric-config-account">
                            <option :value="null">Total Capex</option>
                            <option v-for="r in rm.Accounts" :key="r.Id" :value="r.Id">{{r.Name}}</option>
                        </select>
                    </div>

                    <div class="form-group mb-4 col">
                        <label for="denomination">Currency denomination:</label>
                        <select id="denomination" v-model="denomination" class="form-control" data-testid="capex-metric-config-denomination">
                            <option value="Unit">Unit</option>
                            <option value="Thousand">Thousand</option>
                            <option value="Million">Million</option>
                        </select>
                    </div>
                </div>

                <MetricTimeWindowSettings v-model:startOffset="windowStartOffsetInMonths"
                                          v-model:length="windowLengthInMonths" />

                <MetricScheduleDependentSettings :canDisable="true"
                                                 :applyBenefitsConfidenceFactor="false"
                                                 :showRiskAdjustment="false"
                                                 v-model:isScheduleDependent="isScheduleDependent"
                                                 v-model:defaultProjectStartDate="defaultProjectStartDate"
                                                 v-model:startDateBoundary="startDateBoundary"
                                                 v-model:endDateBoundary="endDateBoundary"
                                                 v-model:valueCommitmentType="valueCommitmentType" />

                <fieldset class="border p-3 mt-4">
                    <legend class="w-auto px-2 text-primary">
                        Spending / Depreciation
                    </legend>

                    <small class="form-text text-muted">
                        Select whether this metric represents spending (cash outlay) or depreciation (asset value reduction over time).
                        This choice affects how the metric is calculated and interpreted in portfolio reports. <strong>Schedule-dependent settings must be enabled.</strong>
                    </small>

                    <div v-if="isScheduleDependent">
                        <div class="form-row mt-3">
                            <div class="form-group mb-4 col">
                                <select class="form-control"
                                        v-model="isDepreciation" data-testid="capex-metric-config-metric-type">
                                    <option :value="false">Spending</option>
                                    <option :value="true">Depreciation</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </fieldset>

            </form>
        </div>
    </div>
</template>
